import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Skeleton } from '@mui/material';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDAvatar from 'components/MDAvatar';

/**
 * Helper to get initials from a name string.
 * @param {string} name - Full name string
 * @returns {string} Initials (e.g., "JD" for "John Doe")
 */
function getInitials(name) {
  if (!name || typeof name !== 'string') return "";
  
  const parts = name.trim().split(" ").filter(part => part.length > 0);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * AdviserDetails component displays detailed information about an adviser.
 * 
 * @param {Object} props - Component props
 * @param {Object} [props.adviserDetails] - Adviser details object containing full_name, email, id_number, branch_name, address
 * @param {boolean} props.loading - Loading state
 * @param {string} [props.error] - Error message to display
 */
export const AdviserDetails = ({ adviserDetails, loading, error }) => {
  if (loading) {
    return (
      <MDBox 
        sx={{ 
          backgroundColor: 'grey.50', 
          borderRadius: 2, 
          p: 3, 
          mb: 4,
          border: '1px solid',
          borderColor: 'grey.200',
          maxWidth: 500,
          width: '100%'
        }}
      >
        <MDBox display="flex" alignItems="center" gap={2} mb={2}>
          <Skeleton variant="circular" width={48} height={48} />
          <MDBox flex={1}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </MDBox>
        </MDBox>
        <MDBox>
          <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="90%" height={20} />
        </MDBox>
      </MDBox>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 4, maxWidth: 500 }}>
        <MDTypography variant="body2">
          {error}
        </MDTypography>
      </Alert>
    );
  }

  if (!adviserDetails) {
    return null;
  }

  return (
    <MDBox 
      sx={{ 
        backgroundColor: 'grey.50', 
        borderRadius: 2, 
        p: 3, 
        mb: 4,
        border: '1px solid',
        borderColor: 'grey.200',
        maxWidth: 500,
        width: '100%'
      }}
    >
      <MDBox display="flex" alignItems="center" gap={2} mb={2}>
        {adviserDetails.profile_pic_path ? (
          <MDAvatar src={adviserDetails.profile_pic_path} name={adviserDetails.full_name} size="sm" />
        ) : (
          <MDAvatar bgColor="info" size="sm">{getInitials(adviserDetails.full_name)}</MDAvatar>
        )}
        <MDBox>
          <MDTypography variant="h6" fontWeight="bold" color="text.primary">
            {adviserDetails.full_name}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox>
        {adviserDetails.email && (
          <MDBox display="flex" alignItems="center" gap={1} mb={1}>
            <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>email</Icon>
            <MDTypography variant="body2" color="text.secondary">
              {adviserDetails.email}
            </MDTypography>
          </MDBox>
        )}
        {adviserDetails.id_number && (
          <MDBox display="flex" alignItems="center" gap={1} mb={1}>
            <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>badge</Icon>
            <MDTypography variant="body2" color="text.secondary">
              ID number: {adviserDetails.id_number}
            </MDTypography>
          </MDBox>
        )}
        {adviserDetails.branch_name && (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>business</Icon>
            <MDTypography variant="body2" color="text.secondary">
              {adviserDetails.branch_name}
            </MDTypography>
          </MDBox>
        )}
        {adviserDetails.address && (
          <MDBox display="flex" alignItems="center" gap={1} mt={1}>
            <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>location_on</Icon>
            <MDTypography variant="body2" color="text.secondary">
              {adviserDetails.address}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
};

AdviserDetails.propTypes = {
  adviserDetails: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
}; 